import { Button, Typography } from "@suraasa/placebo-ui"
import MiniCertificate from "assets/icons/mini-certificate.svg"
import SuitCase from "assets/icons/suit-case.svg"
import ituBadge from "assets/logos/itu-circular-logo.webp"
import ModelPicture from "assets/program-card-model.webp"
import clsx from "clsx"
import suraasaVerifiedBadge from "features/Profile/assets/suraasa-verified-badge.webp"
import { Link } from "react-router-dom"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { PropsWithClassName } from "utils/types"

export const PgctlProgramCard = ({ className }: PropsWithClassName) => {
  return (
    <div
      className={`h-min rounded-[12px] p-0.25 ${className}`}
      style={{
        background:
          "linear-gradient(118.49deg, #FFE159 2.28%, #B977FD 35.85%, #43BDD6 100%)",
        boxShadow: "0px 6px 15px 0px #78A4D033, 0px 1px 2px 0px #4A84BE33",
      }}
    >
      <div className="flex flex-col justify-between rounded-[12px] bg-white p-2.5">
        <div className="flex flex-col">
          <div className="flex flex-row justify-between shadow-[inset_0px_4px_12px_#FFFFFF21]">
            <img
              src={suraasaVerifiedBadge}
              alt="Suraasa Verified Badge"
              className="mb-1 h-4"
            />
            <div className="h-min rounded-lg bg-[#F4D7FF] px-1 py-0.5">
              <Typography
                variant={"body"}
                className="text-[10px] font-medium text-[#442768]"
              >
                Recommended
              </Typography>
            </div>
          </div>
          <Typography variant="strong" className="mb-0.25">
            PgCTL
          </Typography>
          <Typography variant="smallBody" className="mb-2.5 text-onSurface-500">
            Professional Graduate Certificate in Teaching and Learning
          </Typography>

          <Typography variant="smallBody" className="text-onSurface-500">
            10-12 Months • 100% Online
          </Typography>
          <div className="mt-0.5 flex flex-row">
            <img src={MiniCertificate} alt="" className="mr-0.5 h-2" />
            <Typography variant={"smallBody"} className="text-onSurface-500">
              Certificate Upon Completion
            </Typography>
          </div>
          <div className="mt-0.5 flex flex-row">
            <img src={SuitCase} alt="" className="mr-0.5" />
            <Typography variant={"smallBody"} className="text-onSurface-500">
              Job Assistance
            </Typography>
          </div>
        </div>

        <Link
          to={
            "https://www.suraasa.com/qualifications/professional-graduate-certificate-in-teaching-learning"
          }
          target="_blank"
        >
          <Button
            variant={"filled"}
            className="mt-2.5 w-full py-[7px]"
            color="black"
          >
            View Program
          </Button>
        </Link>
      </div>
    </div>
  )
}

export const MedProgramCard = ({ className }: PropsWithClassName) => {
  return (
    <div
      className={`rounded-[12px] bg-white p-2.5 ${className} flex flex-col justify-between`}
      style={{
        boxShadow: "0px 6px 15px 0px #78A4D033, 0px 1px 2px 0px #4A84BE33",
      }}
    >
      <div className="flex flex-col">
        <div className="shadow-[inset_0px_4px_12px_#FFFFFF21]">
          <img src={ituBadge} alt="" className="mb-1 h-4" />
        </div>
        <Typography variant="strong" className="mb-0.25">
          M.Ed.
        </Typography>
        <Typography variant="smallBody" className="mb-2.5 text-onSurface-500">
          Master’s in Education from International Teachers University
        </Typography>

        <Typography variant="smallBody" className="text-onSurface-500">
          12 Months • 100% Online
        </Typography>
        <div className="mt-0.5 flex flex-row">
          <img src={MiniCertificate} alt="" className="mr-0.5 h-2" />
          <Typography variant={"smallBody"} className="text-onSurface-500">
            Master’s Degree Upon Completion
          </Typography>
        </div>
      </div>
      <Link
        to={
          "https://internationalteachers.university/programs/masters-in-education"
        }
        target="_blank"
      >
        <Button
          variant={"outlined"}
          className="mt-2.5 w-full border-gray-300 py-[7px] text-gray-700"
        >
          Learn More
        </Button>
      </Link>
    </div>
  )
}

const GetStartedWithProgramsBanner = ({ className }: PropsWithClassName) => {
  const {
    pgctl: { isPGCTLUser },
    hasMed,
  } = useEnrollments()

  if (!isPGCTLUser && !hasMed)
    return (
      <div className={clsx("relative mt-2", className)}>
        <div className="static mb-2.5 rounded-[18px] bg-surface-100 p-3">
          <div className="flex flex-row">
            <div>
              <Typography variant="preTitle" className="mb-2 text-primary-500">
                GET STARTED
              </Typography>
              <Typography
                variant="title3"
                className="mb-2 text-xl font-semibold"
              >
                Begin your upskilling journey today!
              </Typography>
              <Typography
                variant="smallBody"
                className="mb-2 text-onSurface-500"
              >
                Learn 21st century teaching skills from international experts in
                our comprehensive training programs.
              </Typography>
            </div>
            <img
              src={ModelPicture}
              alt=""
              className="hidden h-[190px] align-bottom lg:block"
            ></img>
          </div>
          <div className="hidden flex-row gap-2.5 md:flex">
            <PgctlProgramCard className="w-1/2" />
            <MedProgramCard className="w-1/2" />
          </div>
          <div className="pointer-events-none mt-8 flex w-full flex-row gap-2.5 overflow-x-auto opacity-0 md:hidden">
            <PgctlProgramCard className="w-3/4 shrink-0 sm:w-2/3" />
            <MedProgramCard className="w-3/4 shrink-0 sm:w-2/3" />
          </div>
        </div>
        <div className="scrollbar-hide absolute -left-5 -right-2 bottom-3 mt-8 flex h-max flex-row gap-2.5 overflow-x-auto px-8 sm:-right-5 md:hidden">
          <PgctlProgramCard className="w-3/4 shrink-0 sm:w-2/3" />
          <MedProgramCard className="w-3/4 shrink-0 sm:w-2/3" />
        </div>
      </div>
    )
}

export default GetStartedWithProgramsBanner
